// The frontend truth table for binding type compatibility — used by the
// picker and the bound-chip warning, mirrored by the backend
// `_types_compatible` (flow_v2/validation.py); change them together.

export type Compatibility = "ok" | "coerced" | "incompatible";

// Aliases still present in stored configs: "dict" for object, and the media
// subtypes the editor no longer offers, which are Files. Mirrored by the
// backend `normalize_type` (flow_v2/type_labels.py); change them together.
const TYPE_ALIASES: Record<string, string> = {
  dict: "object",
  image: "file",
  audio: "file",
};

export function normalizeDataType(
  dataType: string | undefined
): string | undefined {
  if (!dataType) return undefined;
  const lower = dataType.toLowerCase();
  return TYPE_ALIASES[lower] ?? lower;
}

// Object sources fit object/str targets only ("coerced" = stringified to
// JSON); scalar sources (number/bool/datetime) also fit str targets (coerced
// = stringified on resolve); object targets take anything but a File (a File
// is a record, not data to inspect); any targets and unknown types stay
// permissive. List depth is one-way: a scalar field accepts a list (per-item
// iteration), not vice versa.
const STRINGIFIABLE_SOURCES = new Set(["number", "bool", "datetime"]);

export function getCompatibility(
  outputType: string | undefined,
  inputType: string | undefined,
  outputDepth: number,
  inputDepth: number
): Compatibility {
  if (inputDepth > outputDepth) return "incompatible";
  const source = normalizeDataType(outputType);
  const target = normalizeDataType(inputType);
  if (!source || !target) return "ok";
  if (target === "any") return "ok";
  if (source === target) return "ok";
  if (target === "object") return source === "file" ? "incompatible" : "ok";
  if (source === "object") return target === "str" ? "coerced" : "incompatible";
  if (target === "str" && STRINGIFIABLE_SOURCES.has(source)) return "coerced";
  return "incompatible";
}

export function isFileDataType(dataType: string | undefined): boolean {
  return normalizeDataType(dataType) === "file";
}

// An interpolated (`:var[...]`-inside-text) ref is stringified in place, so
// the target is always text: str fits, other scalars/objects are coerced, and a
// File fits only where the node attaches referenced files (its definition's
// `accepts_inline_files`) — elsewhere it would render as a `<file name=...>`
// placeholder.
export function getInlineCompatibility(
  outputType: string | undefined,
  acceptsFiles: boolean
): Compatibility {
  const source = normalizeDataType(outputType);
  if (!source) return "ok";
  if (source === "file") return acceptsFiles ? "ok" : "incompatible";
  if (source === "str" || source === "any") return "ok";
  return "coerced";
}
